import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AppShell from './components/AppShell';
import PlanGate from './components/PlanGate';
import ScrollToTop from './components/ScrollToTop';

// Eagerly loaded — needed for first paint on the public landing AND
// for authenticated users who deep-link straight to /app. We deliberately
// keep DashboardPage eager because the bundle hit is small (~50 KB) and
// keeping it inline saves a chunk roundtrip every time someone opens the
// PWA from a home-screen icon.
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';

// Lazy-loaded — only fetched when user navigates there. This cuts the
// initial JS bundle by ~40% (pdfjs, recharts, and signature pad libs all
// move to their own chunks).
const EnvoicePage        = lazy(() => import('./pages/EnvoicePage'));
const EnvoiceEditor      = lazy(() => import('./pages/EnvoiceEditor'));
const BookkeepingPage    = lazy(() => import('./pages/BookkeepingPage'));
const SchedulePage       = lazy(() => import('./pages/SchedulePage'));
const DocumentsPage      = lazy(() => import('./pages/DocumentsPage'));
const SettingsPage       = lazy(() => import('./pages/SettingsPage'));
const HelpPage           = lazy(() => import('./pages/HelpPage'));
const FeedbackPage       = lazy(() => import('./pages/FeedbackPage'));
const MessagesPage       = lazy(() => import('./pages/MessagesPage'));
const SignupPage         = lazy(() => import('./pages/SignupPage'));
const InviteAcceptPage   = lazy(() => import('./pages/InviteAcceptPage'));
const OnboardingPage     = lazy(() => import('./pages/OnboardingPage'));
const ClientPortalPage   = lazy(() => import('./pages/ClientPortalPage'));
const SignDocumentPage   = lazy(() => import('./pages/SignDocumentPage'));
const PublicInvoiceView  = lazy(() => import('./pages/PublicInvoiceView'));
const AnalyticsPage      = lazy(() => import('./pages/AnalyticsPage'));
const WorkordersPage     = lazy(() => import('./pages/WorkordersPage'));
const MyWorkorderPage    = lazy(() => import('./pages/MyWorkorderPage'));
const PrivacyPolicy      = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService     = lazy(() => import('./pages/legal/TermsOfService'));
const TimeclockPage      = lazy(() => import('./pages/TimeclockPage'));
const RequestsPage       = lazy(() => import('./pages/RequestsPage'));
const FieldCalendarPage = lazy(() => import('./pages/FieldCalendarPage'));
const MyEarningsPage    = lazy(() => import('./pages/MyEarningsPage'));
const TeamTimeclockPage  = lazy(() => import('./pages/TeamTimeclockPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('./pages/ResetPasswordPage'));
const ProjectsPage       = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage  = lazy(() => import('./pages/ProjectDetailPage'));
const CreateProjectWizard = lazy(() => import('./pages/CreateProjectWizard'));
const ClientsPage        = lazy(() => import('./pages/ClientsPage'));
const ChatPage            = lazy(() => import('./pages/ChatPage'));
const WorkHub             = lazy(() => import('./pages/WorkHub'));
const CommissionsPage     = lazy(() => import('./pages/CommissionsPage'));
const SubcontractorsPage = lazy(() => import('./pages/SubcontractorsPage'));
const PayrollPage        = lazy(() => import('./pages/PayrollPage'));
const DispatchPublicPage = lazy(() => import('./pages/DispatchPublicPage'));
const ChoosePlanPage       = lazy(() => import('./pages/ChoosePlanPage'));
const PaymentCompletePage  = lazy(() => import('./pages/PaymentCompletePage'));
const PricingPage          = lazy(() => import('./pages/PricingPage'));
const FeaturesPage         = lazy(() => import('./pages/FeaturesPage'));
const FreeTrialPage        = lazy(() => import('./pages/FreeTrialPage'));
const AuthCallbackPage     = lazy(() => import('./pages/AuthCallbackPage'));
const OpsConsolePage       = lazy(() => import('./pages/OpsConsolePage'));
const ComingSoonPage       = lazy(() => import('./pages/ComingSoonPage'));
import './App.css';
import './density.css';

// Fetch the site-lock state once at app boot. Uses fetch (not the axios
// client) so the request doesn't wait for the AuthContext to hydrate.
// Fail-safe: if the status call fails or the network is down, assume
// LOCKED. This mirrors the backend which also defaults to locked when
// the DB flag is missing.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
function useSiteLock() {
  const [state, setState] = React.useState({ loading: true, locked: true, copy: null });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/system/status`);
        const data = await res.json();
        if (!cancelled) setState({ loading: false, locked: !!data.site_locked, copy: data.coming_soon });
      } catch {
        if (!cancelled) setState({ loading: false, locked: true, copy: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

// Routes that MUST keep working while the site is locked:
//  • Public signing / preview links so already-sent contracts + invoices
//    can still be signed & viewed by clients you invited.
//  • /login so allowlisted team members can get in.
//  • /ops/:slug so ops can toggle the lock back off.
//  • Auth-flow endpoints (invite, forgot/reset, google callback).
//  • Legal pages (privacy/terms).
const LOCK_ALLOWED_PATH_PREFIXES = [
  '/login', '/ops/', '/sign/', '/view/', '/v/', '/dispatch/',
  '/privacy', '/terms', '/auth/', '/invite/',
  '/forgot-password', '/reset-password',
];
function pathIsAllowedWhileLocked(pathname) {
  return LOCK_ALLOWED_PATH_PREFIXES.some(p => pathname === p || pathname.startsWith(p));
}

/**
 * Warm up the lazy bundles right after first paint. The main 5 tabs
 * (Books, Schedule, Files, Analytics, Settings) are the ones users hit
 * most, and waiting for their JS chunk on first tap is what makes nav
 * "feel slow" — this prefetches them during the browser's idle time
 * so subsequent taps render instantly.
 *
 * IMPORTANT: only fire for *authenticated* sessions. Marketing visitors
 * on /, /pricing, /features and the login form should never trigger
 * the app-shell bundles to download — that adds ~600 KB of JS the
 * visitor will never use, slowing first-load on the public site.
 */
let _tabsPrefetched = false;
function prefetchTabs() {
  if (_tabsPrefetched) return;
  _tabsPrefetched = true;
  const tasks = [
    () => import('./pages/BookkeepingPage'),
    () => import('./pages/SchedulePage'),
    () => import('./pages/DocumentsPage'),
    () => import('./pages/AnalyticsPage'),
    () => import('./pages/EnvoiceEditor'),
    () => import('./pages/SettingsPage'),
    () => import('./pages/WorkordersPage'),
    () => import('./pages/PayrollPage'),
    () => import('./pages/SubcontractorsPage'),
    () => import('./pages/TeamTimeclockPage'),
    () => import('./pages/ProfitLeaderboard'),
    () => import('./pages/BackfillBanner'),
  ];
  const run = () => { tasks.forEach(t => { try { t(); } catch { /* swallow */ } }); };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else if (typeof window !== 'undefined') {
    setTimeout(run, 1500);
  }
}

function RouteSpinner() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#B98A2C] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children, blockCrew = false }) {
  const { user, loading } = useAuth();
  React.useEffect(() => { if (user) prefetchTabs(); }, [user]);
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#121A25] flex items-center justify-center transition-colors">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#B98A2C] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 dark:text-[#758A9F] text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (blockCrew && isFieldTech(user)) {
    return <Navigate to={fieldTechHome(user)} replace />;
  }
  return children;
}

// Field Techs (legacy crew/helper roles included) — restricted home is
// driven by pay_type: subcontractor → /my-workorder, hourly → /timeclock.
function isFieldTech(user) {
  const r = (user?.role || '').toLowerCase();
  return r === 'field_tech' || r === 'crew' || r === 'helper';
}

function fieldTechPayType(user) {
  const pt = (user?.pay_type || '').toLowerCase();
  if (pt) return pt;
  // Legacy fallback: crew = subcontractor, helper = hourly
  const r = (user?.role || '').toLowerCase();
  if (r === 'crew') return 'subcontractor';
  if (r === 'helper') return 'hourly';
  return 'hourly';
}

function fieldTechHome(user) {
  return fieldTechPayType(user) === 'subcontractor' ? '/my-workorder' : '/timeclock';
}

function FieldTechRoute({ children, requirePayType }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isFieldTech(user)) return <Navigate to="/app" replace />;
  // If this route only accepts a specific pay type, bounce to the right home.
  if (requirePayType && fieldTechPayType(user) !== requirePayType) {
    return <Navigate to={fieldTechHome(user)} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (isFieldTech(user)) return <Navigate to={fieldTechHome(user)} replace />;
    return <Navigate to="/app" replace />;
  }
  return children;
}

// Root route: if logged in, go to the app dashboard; if not, show the marketing page.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]" />
    );
  }
  if (user) {
    if (isFieldTech(user)) return <Navigate to={fieldTechHome(user)} replace />;
    return <Navigate to="/app" replace />;
  }
  return <LandingPage />;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteSpinner />}>
            <SiteLockedRouter />
          </Suspense>
        </BrowserRouter>
        <Toaster
          position="top-center"
          toastOptions={{
            style: { background: '#1B2838', color: '#fff', border: '1px solid rgba(185,138,44,0.3)', fontFamily: "'Satoshi', sans-serif" },
            className: 'sonner-toast',
          }}
        />
      </ThemeProvider>
    </AuthProvider>
  );
}

/**
 * Wraps the real <Routes> tree with the site-lock gate.
 *
 * When locked, any visitor hitting a "marketing" route (`/`, `/pricing`,
 * `/features`, `/free-trial`, `/signup`, `/app`, `/envoice`, etc.) sees
 * ComingSoonPage instead. Public signing links, /login, /ops, and legal
 * pages remain reachable so real work isn't blocked.
 */
function SiteLockedRouter() {
  const lock = useSiteLock();
  // Reactive pathname so client-side Link nav re-runs the whitelist check.
  const { pathname } = useLocation();
  // Authenticated users always bypass the site lock — the lock is a
  // marketing-shield for anonymous visitors, not a shutter that traps
  // signed-in customers. Without this bypass, a user with a stale JWT
  // in localStorage lands on /login, PublicRoute redirects them to
  // /app, /app isn't in the whitelist, and they get bounced back to
  // Coming Soon — the exact "clicking Sign in just refreshes" symptom.
  const { user, loading: authLoading } = useAuth();

  // While either check is still resolving, render the real tree so we
  // don't paint a Coming Soon flash on every page load. The backend
  // already blocks signup + login independently, so this is safe.
  if (lock.loading || authLoading) return <RealRoutes />;

  if (user) return <RealRoutes />;

  if (lock.locked && !pathIsAllowedWhileLocked(pathname)) {
    return <ComingSoonPage initialCopy={lock.copy} />;
  }
  return <RealRoutes />;
}

function RealRoutes() {
  return (
    <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/free-trial" element={<FreeTrialPage />} />
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/auth/link" element={<AuthCallbackPage />} />

              {/* Mantle Ops Console — private founder dashboard. The
                  slug is a secret env value; any other slug renders a
                  neutral 404 inside OpsConsolePage. Deliberately NOT
                  wrapped in PublicRoute / ProtectedRoute — the page
                  has its own auth. */}
              <Route path="/ops/:slug" element={<OpsConsolePage />} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />
              <Route element={<ProtectedRoute blockCrew={true}><AppShell /></ProtectedRoute>}>
                <Route path="/app" element={<DashboardPage />} />
                <Route path="/envoice" element={<EnvoicePage />} />
                {/* Legacy bare URLs — redirect to the unified Envoice switcher */}
                <Route path="/invoices" element={<Navigate to="/envoice?tab=invoices" replace />} />
                <Route path="/estimates" element={<Navigate to="/projects" replace />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/new" element={<CreateProjectWizard />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/commissions" element={<CommissionsPage />} />
                {/* Editor deep-links (kept for dashboard / email / direct links) */}
                <Route path="/invoices/:id" element={<EnvoiceEditor docType="invoice" />} />
                <Route path="/estimates/:id" element={<EnvoiceEditor docType="estimate" />} />
                <Route path="/bookkeeping" element={<PlanGate feature="bookkeeping"><BookkeepingPage /></PlanGate>} />
                <Route path="/schedule" element={<PlanGate feature="schedule"><SchedulePage /></PlanGate>} />
                <Route path="/files" element={<Navigate to="/documents" replace />} />
                <Route path="/documents" element={<PlanGate feature="documents"><DocumentsPage /></PlanGate>} />
                <Route path="/files/upgrade-documents" element={<PlanGate feature="documents"><div /></PlanGate>} />
                <Route path="/analytics" element={<PlanGate feature="analytics"><AnalyticsPage /></PlanGate>} />
                <Route path="/payroll" element={<PlanGate feature="payroll"><PayrollPage /></PlanGate>} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/subcontractors" element={<Navigate to="/payroll?tab=subcontractors" replace />} />
                <Route path="/team-timeclock" element={<Navigate to="/payroll?tab=timeclock" replace />} />
                {/* Legacy /workorders — redirects to the unified /work hub */}
                <Route path="/workorders" element={<Navigate to="/work?tab=workorders" replace />} />
              </Route>
              {/* /work is the unified Chat + Work Orders hub — accessible to
                  EVERY authenticated user (including field_techs). WorkHub
                  reads ?tab=chat|workorders to decide which body to render,
                  and forces field_techs onto the chat tab. The old /chat
                  route redirects here for backward compat with bookmarks. */}
              <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route path="/work" element={<WorkHub />} />
                <Route path="/chat" element={<Navigate to="/work?tab=chat" replace />} />
              </Route>
              <Route path="/my-workorder" element={<FieldTechRoute requirePayType="subcontractor"><MyWorkorderPage /></FieldTechRoute>} />
              <Route path="/timeclock" element={<FieldTechRoute requirePayType="hourly"><TimeclockPage /></FieldTechRoute>} />
              <Route path="/requests" element={<FieldTechRoute><RequestsPage /></FieldTechRoute>} />
              <Route path="/calendar" element={<FieldTechRoute><FieldCalendarPage /></FieldTechRoute>} />
              <Route path="/my-earnings" element={<FieldTechRoute requirePayType="subcontractor"><MyEarningsPage /></FieldTechRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
              <Route path="/onboarding/choose-plan"      element={<ProtectedRoute><ChoosePlanPage /></ProtectedRoute>} />
              <Route path="/onboarding/payment-complete" element={<ProtectedRoute><PaymentCompletePage /></ProtectedRoute>} />
              <Route path="/portal/:email" element={<ClientPortalPage />} />
              <Route path="/sign/:token" element={<SignDocumentPage />} />
              <Route path="/view/:token" element={<PublicInvoiceView />} />
              <Route path="/v/:token" element={<PublicInvoiceView />} />
              <Route path="/dispatch/:token" element={<DispatchPublicPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
  );
}

export default App;
